import React, { useState } from "react";
import axios from "axios";
import { baseURL } from "../backend_url";
import {
  Container,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
  Chip,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import ListIcon from '@mui/icons-material/List';

export default function EnhancedRateSearchForm() {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    pickupLocation: "",
    pickupLocationName: "",
    dropoffLocation: "",
    dropoffLocationName: "",
    pickupDate: "",
    pickupTime: "",
    dropoffDate: "",
    dropoffTime: "",
    locationSearchPhrase: "",
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        pickupDateTime: `${formData.pickupDate} ${formData.pickupTime}`,
        returnDateTime: `${formData.dropoffDate} ${formData.dropoffTime}`
      };
      const res = await axios.post(
        `${baseURL}/fetchratesViaScrapper`,
        payload
      );
      setResults(res.data);
    } catch (err) {
      console.error("Error fetching rates:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      {/* Form Card */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom p={1} fontWeight="bold">
            🚗 AceRentACar Rate Search
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt:2 }}>
            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="pickupLocation"
                  label="Pickup Code"
                  placeholder="e.g. YYZ"
                  size="small"
                  fullWidth
                  value={formData.pickupLocation}
                  onChange={handleChange}
                  InputProps={{ startAdornment: <LocationOnIcon sx={{ mr:1 }} /> }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="pickupLocationName"
                  label="Pickup Location"
                  size="small"
                  fullWidth
                  value={formData.pickupLocationName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="dropoffLocation"
                  label="Dropoff Code"
                  placeholder="e.g. YYC"
                  size="small"
                  fullWidth
                  value={formData.dropoffLocation}
                  onChange={handleChange}
                  InputProps={{ startAdornment: <LocationOnIcon sx={{ mr:1 }} /> }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="dropoffLocationName"
                  label="Dropoff Location"
                  size="small"
                  fullWidth
                  value={formData.dropoffLocationName}
                  onChange={handleChange}
                  required
                />
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  name="pickupDate"
                  label="Pickup Date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <EventIcon sx={{ mr:1 }} /> }}
                  value={formData.pickupDate}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="time"
                  name="pickupTime"
                  label="Pickup Time"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <AccessTimeIcon sx={{ mr:1 }} /> }}
                  value={formData.pickupTime}
                  onChange={handleChange}
                  placeholder="HH:MM"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  name="dropoffDate"
                  label="Dropoff Date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <EventIcon sx={{ mr:1 }} /> }}
                  value={formData.dropoffDate}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="time"
                  name="dropoffTime"
                  label="Dropoff Time"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <AccessTimeIcon sx={{ mr:1 }} /> }}
                  value={formData.dropoffTime}
                  onChange={handleChange}
                  placeholder="HH:MM"
                  required
                />
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12} sm={9} md={9}>
                <TextField
                  name="locationSearchPhrase"
                  label="Search Phrase (Optional)"
                  size="small"
                  fullWidth
                  value={formData.locationSearchPhrase}
                  onChange={handleChange}
                  InputProps={{ startAdornment: <ListIcon sx={{ mr:1 }} /> }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  loading={loading}
                  loadingPosition="start"
                  startIcon={<SearchIcon />}
                  sx={{ height: '100%' }}
                >
                  Get Rates
                </LoadingButton>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ zIndex: theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress size={64} />
      </Backdrop>

      {/* Results */}
      {results.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            📋 Available Cars
          </Typography>
          <Grid container spacing={3}>
            {results.map((car, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Card elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {car.name} <Chip label={car.code} size="small" sx={{ ml:1 }} />
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt:1, mb:1 }}>
                    💲 {car.totalPayLaterEstimatedCharge}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Category: <Chip label={car.category} size="small" />
                  </Typography>
                  <Box sx={{ display: 'flex', gap:1, mt:1 }}>
                    <Chip label={`Seats: ${car.passengerCount}`} size="small"/>
                    <Chip label={`Bags: ${car.baggageCount}`} size="small"/>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
}
